import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

import {
    ISchedule,
    ModalType,
    defaultSchedule,
} from "Pages/Setup/Scheduler/Scheduler";
import useAlertContext from "Hooks/useAlertContext";
import useFetch from "Hooks/useFetch";
import { useRef, useEffect } from "react";
import Draggable from "react-draggable";

interface IProps {
    activeModal: ModalType;
    getSchedules: () => void;
    onSetActiveModal: (modal: ModalType) => void;
    scheduleClicked: ISchedule;
    onSetScheduleClicked: (schedule: ISchedule) => void;
}

export default function DeleteSchedule({
    activeModal,
    getSchedules,
    onSetActiveModal,
    scheduleClicked,
    onSetScheduleClicked,
}: IProps) {
    const { deleteRequest } = useFetch();
    const { newAlert } = useAlertContext();

    /** Tracks whether we are editing one schedule that represents multiple schedules on AWS EventBridge
     * due to converting from older Windows Task Scheduler system to newer AWS EventBridge system. If multiple,
     * the edit request goes to its own endpoint to handle deleting the extra schedules and creating a new one.
     */
    const multipleSchedules = useRef<boolean | null>(null);
    // Catch a true value of scheduleClicked.trigger.includes(";") (indicating multiple triggers/schedules)
    // after state update in parent completes. Only set the ref if it is null to prevent setting it after
    // child CronSelector changes the scheduleClicked state.
    useEffect(() => {
        if (
            multipleSchedules.current === null &&
            scheduleClicked.trigger !== ""
        ) {
            multipleSchedules.current = scheduleClicked.trigger.includes(";");
        }
    }, [multipleSchedules, scheduleClicked.trigger]);

    const closeModal = () => {
        onSetActiveModal("");
        multipleSchedules.current = null;
        onSetScheduleClicked(defaultSchedule);
    };

    const deleteSchedule = async () => {
        const url = multipleSchedules.current
            ? `/api/schedules/${scheduleClicked.name}`
            : `/api/schedule/${scheduleClicked.name}`;

        const response = await deleteRequest(url);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Delete schedule response: ", data);

        if (response.ok) {
            newAlert({
                message: "Schedule deleted successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Unable to delete schedule",
                color: "danger",
            });
        }
        closeModal();
    };

    const nameShown = scheduleClicked?.name?.replace(/_+/g, " ");

    return (
        <Draggable>
            <Modal isOpen={activeModal === "delete"} toggle={closeModal}>
                <ModalHeader toggle={closeModal}>Delete Schedule</ModalHeader>
                <ModalBody>Are you sure you want to delete {nameShown}?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={deleteSchedule}>
                        Confirm
                    </Button>
                </ModalFooter>
            </Modal>
        </Draggable>
    );
}
